/**
 * Sets map bearing from device orientation when the browser exposes compass data.
 * 
 * @typedef L.Map.CompassBearing
 * @property {L.Map.CompassBearing} compassBearing Map handler, enabled to follow device heading.
 * 
 * @since leaflet-rotate (v0.1.0)
 */

L.Map.CompassBearing = L.Handler.extend({

    /**
     * @param {L.Map} map Map whose bearing follows device orientation.
     * 
     * @since leaflet-rotate (v0.1.0)
     */
    initialize(map) {
        this._map = map;
        /** @see https://caniuse.com/?search=DeviceOrientation */
        if ('ondeviceorientationabsolute' in window) {
            this.__deviceOrientationEvent = 'deviceorientationabsolute';
        } else if('ondeviceorientation' in window) {
            this.__deviceOrientationEvent = 'deviceorientation';
        }
        this._throttled = L.Util.throttle(this._onDeviceOrientation, 100, this);
    },

    /**
     * Subscribe to the supported device-orientation event, if available.
     * On iOS, permission is requested first (call `enable()` from a user gesture).
     * 
     * @since leaflet-rotate (v0.1.0)
     */
    addHooks() {
        if (this._map._rotate && this.__deviceOrientationEvent) {
            L.DomEvent.on(window, this.__deviceOrientationEvent, this._throttled, this);
            const DOE = window.DeviceOrientationEvent;
            if (DOE && DOE.requestPermission) {
                DOE.requestPermission()
                    .then(state => 'denied' === state && this.enabled() && this.disable())
                    .catch(() => {});
            }
        } else {
            // L.Map.CompassBearing handler will be automatically
            // disabled if device orientation is not supported.
            this.disable();
        }
    },

    /**
     * Unsubscribe from device orientation when the handler is disabled.
     * 
     * @since leaflet-rotate (v0.1.0)
     */
    removeHooks() {
        if (this._map._rotate && this.__deviceOrientationEvent) {
            L.DomEvent.off(window, this.__deviceOrientationEvent, this._throttled, this);
        }
    },

    /**
     * Apply a device orientation reading to the map bearing.
     *
     * `DeviceOrientationEvent.absolute` - Indicates whether the device is providing absolute
     *                                     orientation values (relatives to Magnetic North) or
     *                                     using some arbitrary frame determined by the device.
     * 
     * `DeviceOrientationEvent.alpha`    - Returns the rotation of the device around the Z axis;
     *                                     that is, the number of degrees by which the device is
     *                                     being twisted around the center of the screen.
     * 
     * `window.orientation`              - Returns the screen orientation in degrees (in 90-degree increments)
     *                                     of the viewport relative to the device's natural orientation.
     *                                     Its only possible values are -90, 0, 90, and 180. Positive
     *                                     values are counterclockwise; negative values are clockwise.
     * 
     * @see https://developer.mozilla.org/en-US/docs/Web/API/DeviceOrientationEvent/absolute
     * @see https://developer.mozilla.org/en-US/docs/Web/API/DeviceOrientationEvent/alpha
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/orientation
     *
     * @param {DeviceOrientationEvent} e Orientation reading from the browser.
     * 
     * @since leaflet-rotate (v0.1.0)
     */
    _onDeviceOrientation(e) {
        let angle = e.webkitCompassHeading != null ? e.webkitCompassHeading : e.alpha;
        let deviceOrientation = 0;

         if (!Number.isFinite(angle)) { return; }

         // Safari iOS
         if (!e.absolute && e.webkitCompassHeading) {
            angle = 360 - angle;
        }

        // Older browsers
        if (!e.absolute && 'undefined' !== typeof window.orientation) {
            deviceOrientation = window.orientation;
        }

        this._map.setBearing(angle - deviceOrientation);
    },

});

/**
 * Add Compass bearing handler to L.Map (disabled unless `window.DeviceOrientationEvent` is set).
 * 
 * @property {L.Map.CompassBearing} compassBearing
 * 
 * @since leaflet-rotate (v0.1.0)
 */
L.Map.addInitHook('addHandler', 'compassBearing', L.Map.CompassBearing);
